import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Percent, Sparkles } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { ADDONS, IMAGES } from "@/data/content";

const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const split = (rate, hours) => {
  const net = rate * hours;
  const partnerPct = hours >= 16 ? 60 : 40;
  const partner = (net * partnerPct) / 100;
  return { net, partnerPct, partner, xtreme: net - partner, boosted: hours >= 16 };
};

const EXAMPLES = [
  { hours: 15, rate: 350 },
  { hours: 20, rate: 350 },
];

export const Revenue = () => {
  const [rate, setRate] = useState(350);
  const [hours, setHours] = useState(15);
  const r = useMemo(() => split(rate, hours), [rate, hours]);

  return (
    <>
      <section id="revenue" className="relative overflow-hidden py-24 lg:py-32" data-testid="revenue-section">
        <div className="spotlight absolute inset-0" />
        <div className="container-x relative">
          <Reveal>
            <Chapter index="03" label="Revenue Opportunity" />
            <div className="grid items-end gap-6 lg:grid-cols-[1.5fr_1fr]">
              <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
                A Performance-Based <span className="text-aqua">Revenue Opportunity</span>
              </h2>
              <p className="text-base leading-relaxed text-muted lg:text-right">
                One official weekly model. The harder the asset works, the more of the week you keep.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="card-line rounded-2xl p-7 sm:p-9" data-testid="revenue-calculator">
                <div className="mb-8">
                  <div className="mb-3 flex items-baseline justify-between">
                    <label htmlFor="rate-slider" className="field-label !mb-0">Hourly rental rate</label>
                    <span className="font-display text-2xl font-bold text-aqua" data-testid="rate-display">{fmt(rate)}/hr</span>
                  </div>
                  <input
                    id="rate-slider"
                    type="range"
                    min="200"
                    max="600"
                    step="10"
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value))}
                    className="w-full accent-[#00FCF5]"
                    data-testid="revenue-calculator-rate-slider"
                  />
                </div>
                <div className="mb-2">
                  <div className="mb-3 flex items-baseline justify-between">
                    <label htmlFor="hours-slider" className="field-label !mb-0">Completed weekly rental hours</label>
                    <span className="font-display text-2xl font-bold text-aqua" data-testid="hours-display">{hours} hrs</span>
                  </div>
                  <input
                    id="hours-slider"
                    type="range"
                    min="1"
                    max="40"
                    step="1"
                    value={hours}
                    onChange={(e) => setHours(Number(e.target.value))}
                    className="w-full accent-[#00FCF5]"
                    data-testid="revenue-calculator-hours-slider"
                  />
                </div>

                {r.boosted && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-5 flex items-center gap-2 rounded-lg border border-aqua/40 bg-aqua/10 px-4 py-2.5 text-xs font-semibold tracking-wide text-aqua"
                    data-testid="retroactive-badge"
                  >
                    <Sparkles size={14} /> 60% partner share unlocked — applied retroactively to all {hours} hours this week
                  </motion.div>
                )}

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-black/[0.07] bg-ink/60 p-5">
                    <p className="field-label">Estimated Net Rental Revenue</p>
                    <p className="font-display text-3xl font-extrabold text-offwhite" data-testid="calc-net-revenue">{fmt(r.net)}</p>
                    <p className="mt-1 text-xs text-muted">per weekly settlement period</p>
                  </div>
                  <div className="rounded-xl border border-black/[0.07] bg-ink/60 p-5">
                    <p className="field-label">Fleet Partner Percentage</p>
                    <p className="font-display text-3xl font-extrabold text-offwhite" data-testid="calc-partner-pct">{r.partnerPct}%</p>
                    <p className="mt-1 text-xs text-muted">{hours >= 16 ? "16+ hours tier" : "1–15 hours tier"}</p>
                  </div>
                  <div className="rounded-xl border border-aqua/30 bg-aqua/[0.07] p-5">
                    <p className="field-label !text-aqua">Estimated Fleet Partner Share</p>
                    <p className="font-display text-3xl font-extrabold text-aqua" data-testid="revenue-calculator-partner-payout">{fmt(r.partner)}</p>
                    <p className="mt-1 text-xs text-muted">{r.partnerPct}% of Net Rental Revenue</p>
                  </div>
                  <div className="rounded-xl border border-black/[0.07] bg-ink/60 p-5">
                    <p className="field-label">Estimated Xtreme Exotics Share</p>
                    <p className="font-display text-3xl font-extrabold text-offwhite" data-testid="calc-xtreme-share">{fmt(r.xtreme)}</p>
                    <p className="mt-1 text-xs text-muted">{100 - r.partnerPct}% of Net Rental Revenue</p>
                  </div>
                </div>

                <p className="mt-7 text-[11px] leading-relaxed text-muted/70" data-testid="calc-disclosure">
                  Examples are illustrative and do not guarantee revenue. Actual results depend on pricing, completed
                  eligible rental hours, weather, seasonality, customer demand, refunds, chargebacks, taxes, fees and
                  the terms of the final Fleet Partner Agreement.
                </p>
              </div>
            </Reveal>

            <div className="space-y-6 lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="card-line rounded-2xl p-7">
                  <h3 className="mb-5 flex items-center gap-2 font-display text-base font-bold text-offwhite">
                    <Percent size={16} className="text-aqua" /> The Official Weekly Model
                  </h3>
                  <div className="space-y-4 text-sm leading-relaxed text-muted">
                    <p>
                      <strong className="text-offwhite">1–15 completed hours</strong> per Jet Car in a weekly settlement
                      period: Fleet Partner receives <strong className="text-aqua">40%</strong> of Net Rental Revenue,
                      Xtreme Exotics receives 60%.
                    </p>
                    <p>
                      <strong className="text-offwhite">16+ completed hours</strong> in the same week: Fleet Partner
                      receives <strong className="text-aqua">60%</strong>, Xtreme Exotics receives 40% — applied
                      retroactively to every eligible completed hour that week, not only the hours above 15.
                    </p>
                    <p className="text-xs text-muted/80">The calculation resets at the beginning of each new weekly settlement period.</p>
                  </div>
                </div>
              </Reveal>

              {EXAMPLES.map((ex, i) => {
                const e = split(ex.rate, ex.hours);
                return (
                  <Reveal key={ex.hours} delay={0.15 + i * 0.08}>
                    <div className="card-line rounded-2xl p-6" data-testid={`example-card-${ex.hours}`}>
                      <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                        <TrendingUp size={13} className="text-aqua" /> Example · {ex.hours} completed hours at {fmt(ex.rate)}/hr
                      </p>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted/70">Net Revenue</p>
                          <p className="mt-1 font-display text-lg font-bold text-offwhite">{fmt(e.net)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted/70">Partner {e.partnerPct}%</p>
                          <p className="mt-1 font-display text-lg font-bold text-aqua">{fmt(e.partner)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted/70">Xtreme {100 - e.partnerPct}%</p>
                          <p className="mt-1 font-display text-lg font-bold text-offwhite">{fmt(e.xtreme)}</p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-charcoal/30 py-24 lg:py-32" data-testid="addons-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Chapter index="04" label="Partner-Created Add-Ons" />
            <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Create Additional Revenue <span className="text-aqua">Around the Experience</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted">
              Fleet Partners may keep <strong className="text-offwhite">100% of approved add-on revenue</strong> they
              create, subject to the Fleet Partner Agreement. Build premium packages around the moments your customers
              already celebrate on the water.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {ADDONS.map((a) => (
                <li key={a} className="flex items-center gap-3 text-sm text-offwhite/90">
                  <span className="h-1.5 w-1.5 rotate-45 bg-aqua" /> {a}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-muted/70">
              Add-ons require approval and are not automatically included in the program.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl border border-aqua/15" aria-hidden="true" />
              <img
                src={IMAGES.jetAction}
                alt="White and red Jet Car carrying two passengers across open water at golden hour"
                className="aspect-[4/3] w-full rounded-xl object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};
